import type { KeyboardEvent, ReactNode } from "react";
import type { ModalProps } from "../ui";
import { KEY_ENTER } from "../ui/keyboardKeys";

const ARIA_TRUE = "true";

const defaultDialogActionSelectors = [
  ".ck-modal-footer .ck-btn--primary:not(.ck-btn--danger)",
  // Eigene Knopfbausteine eines Produkts kennzeichnen ihre Hauptaktion produktneutral.
  ".ck-modal-footer [data-primary-action]",
  ".ck-modal-footer .ck-btn:not(.ck-btn--danger)",
];

const localEnterOwnerSelector = [
  "textarea",
  "[contenteditable='true']",
  ".ck-select",
  ".ck-picker",
  ".ck-upload",
  ".ck-input-number",
  "[role='combobox']",
  "[role='spinbutton']",
].join(",");

const nativeKeyboardActionSelector = [
  "button",
  "a[href]",
  "input[type='button']",
  "input[type='submit']",
  "input[type='reset']",
].join(",");

function canClickElement(element: HTMLElement): boolean {
  return !(
    element.hasAttribute("disabled") ||
    element.getAttribute("aria-disabled") === ARIA_TRUE ||
    element.classList.contains("is-loading")
  );
}

function findDefaultDialogAction(root: HTMLElement): HTMLElement | null {
  for (const selector of defaultDialogActionSelectors) {
    const candidates = root.querySelectorAll<HTMLElement>(selector);
    for (const candidate of Array.from(candidates)) {
      if (canClickElement(candidate)) return candidate;
    }
  }
  return null;
}

function shouldHandleDialogEnter(event: KeyboardEvent<HTMLElement>): boolean {
  if (
    event.key !== KEY_ENTER ||
    event.defaultPrevented ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    event.nativeEvent.isComposing
  ) {
    return false;
  }

  const target = event.target instanceof HTMLElement ? event.target : null;
  if (!target) return true;

  if (target.closest("form")) return false;
  if (target.closest(localEnterOwnerSelector)) return false;
  if (target.closest(nativeKeyboardActionSelector)) return false;

  return true;
}

export function handleDialogEnterKeyDown(event: KeyboardEvent<HTMLElement>) {
  if (!shouldHandleDialogEnter(event)) return;

  const action = findDefaultDialogAction(event.currentTarget);
  if (!action) return;

  event.preventDefault();
  action.click();
}

export function createDialogKeyboardModalRender(modalRender?: ModalProps["modalRender"]): ModalProps["modalRender"] {
  return (node: ReactNode) => {
    const wrapped = (
      <div data-mq-dialog-keyboard="true" onKeyDown={handleDialogEnterKeyDown}>
        {node}
      </div>
    );
    return modalRender ? modalRender(wrapped) : wrapped;
  };
}
